/* eslint-disable no-shadow */
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _, { now } from 'lodash';
import {
    Table, Checkbox as RSCheckbox, Pagination,
} from 'rsuite';
import { Search } from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    AccordionActions,
    Divider,
    Avatar,
    Typography,
    Grid,
    TextField,
    InputAdornment,
} from '@material-ui/core';
import AutoComplete from '@material-ui/lab/Autocomplete';
// @ts-ignore
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { dynamicSort } from '@commonClient2/services/sort';
import ObsoleteRangePicker from '@commonClient2/components/ObsoleteRangePicker';
import { ColumnFilter } from '@commonClient2/components';
import {
    selectPeriod, selectHyperfind, UPDATE_MANAGER_APP_DATA,
} from '../../store/appData';
import {
    UPDATE_DRAFT_EMPLOYEES_SELECTION,
    saveEmployeeSelectionStepDraft,
} from '../../store/bidProfileDraft';
import {
    UPDATE_EMPLOYEES_SELECTION,
    saveEmployeeSelectionStep,
} from '../../store/bidProfile';
import './EmployeeSelection.scss';

import DataviewService from '../../../services/DataviewService';
import { EmployeeDataviewTooltip, ManagerWarning } from '../../../components';
import { BidProfileStatus } from '../../../Enums';
import { defaultSortType, employeeNameDataKey, getSortPathByKey } from '../../services/EmployeeSortingHelper';

const { Column, HeaderCell, Cell } = Table;

const pageSizeOptions = [10, 25, 50, 100, 200];
const initialPageSize = 10;

const Fields = Object.freeze({
    BidSetupId: 'bidSetupId',
    HyperfindId: 'hyperfindId',
    PeriodSelected: 'periodSelected',
    EmployeesIds: 'employeesIds',
});

/**
 * @param {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile} profile
 * @returns {boolean}
 */
export const isShowEmployeeSelectionWarning = (profile) => !!profile.managerWarnings.length
    && (profile.status !== BidProfileStatus.Draft || profile.employees.isCompleted);

function EmployeeSelection() {
    const dispatch = useDispatch();
    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { managerAppData, bidProfileDraft, bidProfile, publicConfigs } = useSelector((state) => state);
    const {
        dataviewEmployeeData, bidProfileDrafts, bidProfiles, hyperfinds, managerBidSetups,
    } = managerAppData;
    const { isEmployeeSelectionDateRangeRequired } = publicConfigs;

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const allowedSymbolicPeriods = managerAppData.periods.filter((x) => x.startDate >= currentDate || x.label.includes('Current'));
    const { t } = useTranslation('shiftBidding');
    const [validationErrors, setValidationErrors] = useState({
        bidSetupId: false, hyperfindId: false, periodSelected: false, employeesIds: false,
    });

    /**
     * @type {[string, (searchValue: string) => void]} SearchValue
     */
    const [searchValue, setSearchValue] = useState('');
    const filteredEmployees = DataviewService.searchInDataviews(managerAppData.filteredDataviewEmployeeData, searchValue);

    const [sortType, setSortType] = useState(defaultSortType);
    const [sortColumn, setSortColumn] = useState(employeeNameDataKey);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [pageIndex, setPageIndex] = useState(1);
    
    const sortPath = getSortPathByKey(filteredEmployees, sortColumn);
    const sortKey = sortType === 'desc' ? `-${sortPath}` : sortPath;

    const sortedData = useMemo(
        () => filteredEmployees?.sort(dynamicSort(sortKey)),
        [JSON.stringify(filteredEmployees), sortType, sortColumn],
    );

    const paginatedData = sortedData.filter((v, i) => {
        const start = pageSize * (pageIndex - 1);
        const end = start + pageSize;
        return i >= start && i < end;
    });

    /**
     * @param {number|undefined} bidSetupId
     */
    const getBidSetup = (bidSetupId) => {
        if (!bidSetupId) {
            return false;
        }
        const value = managerAppData.managerBidSetups.find((x) => x.bidSetupId === bidSetupId);
        if (!value) {
            return null;
        }
        return value;
    };

    /**
     * @param {number|undefined} hyperfindId
     */
    const validateHyperfindId = (hyperfindId) => {
        if (!hyperfindId) {
            return false;
        }
        const value = managerAppData.hyperfinds.find((x) => x.value === hyperfindId);
        if (!value) {
            return false;
        }
        return true;
    };

    /**
     * @param {ManagerAppData.ISelectedPeriod} periodSelected
     */
    const validatePeriod = (periodSelected) => periodSelected && periodSelected.startDate && periodSelected.endDate;

    /**
     * @param {string[]} employeesIds
     */
    const validateEmployeeIds = (employeesIds) => employeesIds && employeesIds.length;

    /**
     * @param {string} field
     * @param {any} value
     */
    const removeValidationError = (field, value) => {
        switch (field) {
            case Fields.BidSetupId:
                if (validationErrors.bidSetupId) {
                    if (getBidSetup(value)) {
                        const errors = { ...validationErrors, bidSetupId: false };
                        setValidationErrors(errors);
                    }
                }
                break;
            case Fields.HyperfindId:
                if (validationErrors.hyperfindId) {
                    if (validateHyperfindId(value)) {
                        const errors = { ...validationErrors, hyperfindId: false };
                        setValidationErrors(errors);
                    }
                }
                break;
            case Fields.PeriodSelected:
                if (validationErrors.periodSelected) {
                    if (validatePeriod(value)) {
                        const errors = { ...validationErrors, periodSelected: false };
                        setValidationErrors(errors);
                    }
                }
                break;
            case Fields.EmployeesIds:
                if (validationErrors.employeesIds) {
                    if (validateEmployeeIds(value)) {
                        const errors = { ...validationErrors, employeesIds: false };
                        setValidationErrors(errors);
                    }
                }
                break;
            default:
                break;
        }
    };

    /**
     * @param {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile} profile
     * @returns
     */
    const validate = (profile) => {
        let hasError = false;
        const errors = {
            bidSetupId: false, hyperfindId: false, periodSelected: false, employeesIds: false,
        };
        const { bidSetupId } = profile.originalBidProfileInformation;
        const { hyperfindId, periodSelected, employeesIds } = profile.employees;

        if (!getBidSetup(bidSetupId)) {
            hasError = true;
            errors.bidSetupId = true;
        }

        if (!validateHyperfindId(hyperfindId)) {
            hasError = true;
            errors.hyperfindId = true;
        }

        if (isEmployeeSelectionDateRangeRequired && !validatePeriod(periodSelected)) {
            hasError = true;
            errors.periodSelected = true;
        }

        if (!validateEmployeeIds(employeesIds)) {
            hasError = true;
            errors.employeesIds = true;
        }

        if (errors.bidSetupId !== validationErrors.bidSetupId
            || errors.hyperfindId !== validationErrors.hyperfindId
            || errors.periodSelected !== validationErrors.periodSelected
            || errors.employeesIds !== validationErrors.employeesIds) {
            setValidationErrors(errors);
        }

        return !hasError;
    };

    /**
     * @param {number} value
     * @param {boolean} checked
     */
    // eslint-disable-next-line no-unused-vars
    let handleCheck = (value, checked) => { };

    /**
     * @param {number} _
     * @param {boolean} checked
     */
    // eslint-disable-next-line no-unused-vars
    let handleCheckAll = (_, checked) => { };

    /**
     * @param {string} target
     * @param {ManagerAppData.IHyperfind|null} selectedOption
     */
    // eslint-disable-next-line no-unused-vars
    let handleHyperfindSelectionChange = (target, selectedOption) => { };

    let handleConfirm = () => { };
    let handleDiscard = () => { };

    /**
     * @param {boolean} expanded
     */
    // eslint-disable-next-line no-unused-vars
    let toggleAccordion = (expanded) => { };

    /**
     * @type {string[]}
     */
    let checkedEmployees = [];

    /**
     * @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile}
     */
    let profile;
    let isDraft = false;
    let canEditEmployees;
    if (bidProfileDraft.id !== '') {
        profile = bidProfileDraft;
        isDraft = true;
        canEditEmployees = true;

        checkedEmployees = bidProfileDraft.employees.employeesIds;

        handleCheck = (value, checked) => {
            const nextCheckedKeys = checked ? [...checkedEmployees, value] : checkedEmployees.filter((id) => id !== value);
            removeValidationError(Fields.EmployeesIds, nextCheckedKeys);
            dispatch({
                type: UPDATE_DRAFT_EMPLOYEES_SELECTION,
                payload: { employeesIds: nextCheckedKeys },
            });
        };

        handleCheckAll = (_, checked) => {
            const nextCheckedKeys = checked ? dataviewEmployeeData.map((e) => e.employeeId) : [];
            removeValidationError(Fields.EmployeesIds, nextCheckedKeys);
            dispatch({ type: UPDATE_DRAFT_EMPLOYEES_SELECTION, payload: { employeesIds: nextCheckedKeys } });
        };

        handleHyperfindSelectionChange = (target, selectedOption) => {
            removeValidationError(target, selectedOption?.value);
            dispatch(
                selectHyperfind(
                    true,
                    bidProfileDraft.draftId,
                    selectedOption?.value || undefined,
                    selectedOption?.label || undefined,
                    bidProfileDraft.employees.periodSelected,
                    bidProfileDraft.originalBidProfileInformation.bidSetupId,
                    { managerBidSetups },
                ),
            );
        };

        handleConfirm = () => {
            if (validate(bidProfileDraft)) {
                dispatch(saveEmployeeSelectionStepDraft(bidProfileDraft.draftId, {
                    bidProfileDrafts,
                    // @ts-ignore
                    employees: bidProfileDraft.employees,
                    hyperfinds,
                }));
            }
        };

        handleDiscard = () => {
            dispatch({
                type: UPDATE_DRAFT_EMPLOYEES_SELECTION,
                payload: _.cloneDeep({ ...bidProfileDraft.originalEmployees, isExpanded: true }),
            });
        };

        toggleAccordion = (expanded) => {
            dispatch({ type: UPDATE_DRAFT_EMPLOYEES_SELECTION, payload: { isExpanded: expanded } });
        };
    } else if (bidProfile.id !== '') {
        profile = bidProfile;
        isDraft = false;
        const bidSetup = getBidSetup(profile.originalBidProfileInformation.bidSetupId);
        canEditEmployees = !!(bidSetup?.isEditEmployeesWhenBidActive);

        checkedEmployees = bidProfile.employees.employeesIds;

        handleCheck = (value, checked) => {
            const nextCheckedKeys = checked ? [...checkedEmployees, value] : checkedEmployees.filter((id) => id !== value);
            removeValidationError(Fields.EmployeesIds, nextCheckedKeys);
            dispatch({
                type: UPDATE_EMPLOYEES_SELECTION,
                payload: { employeesIds: nextCheckedKeys },
            });
        };

        handleCheckAll = (_, checked) => {
            const nextCheckedKeys = checked ? dataviewEmployeeData.map((e) => e.employeeId) : [];
            removeValidationError(Fields.EmployeesIds, nextCheckedKeys);
            dispatch({ type: UPDATE_EMPLOYEES_SELECTION, payload: { employeesIds: nextCheckedKeys } });
        };

        handleHyperfindSelectionChange = (target, selectedOption) => {
            removeValidationError(target, selectedOption?.value);
            dispatch(selectHyperfind(
                false,
                bidProfile.profileId,
                selectedOption?.value || undefined,
                selectedOption?.label || undefined,
                bidProfile.employees.periodSelected,
                bidProfile.originalBidProfileInformation.bidSetupId,
                { managerBidSetups },
            ));
        };

        handleConfirm = () => {
            if (validate(bidProfile)) {
                dispatch(saveEmployeeSelectionStep(
                    bidProfile.profileId,
                    {
                        bidProfiles,
                        employees: bidProfile.employees,
                        hyperfinds,
                        scheduleTemplates: bidProfile.scheduleTemplates,
                    },
                ));
            }
        };

        handleDiscard = () => {
            dispatch({
                type: UPDATE_EMPLOYEES_SELECTION,
                payload: _.cloneDeep({ ...bidProfile.originalEmployees, isExpanded: true }),
            });
        };

        toggleAccordion = (expanded) => {
            dispatch({ type: UPDATE_EMPLOYEES_SELECTION, payload: { isExpanded: expanded } });
        };
    }

    // @ts-ignore
    if (profile === undefined) {
        return <></>;
    }

    const {
        employees,
        isRebid,
        managerWarnings,
        isCreationAllowed,
        isViewAllowed,
    } = profile;
    
    const hasOnlyViewAccess = isViewAllowed && !isCreationAllowed;
    const isInputDisabled = !employees.isEditable || hasOnlyViewAccess;

    const isSelectAllChecked = checkedEmployees.length === dataviewEmployeeData.length;
    const isSelectAllIndeterminate = !!(!isSelectAllChecked
        && checkedEmployees.length
        && checkedEmployees.length < dataviewEmployeeData.length);

    /** @param {{rowData: ManagerAppData.IEmployee}} rowData */
    const NameCell = ({ rowData, ...props }) => { // TODO KAT 06/16: What fields needs to be displayed in tooltip?
        const row = dataviewEmployeeData.find((x) => x.employeeId === rowData.employeeId);
        if (!row) { // No dataviews found -> no tooltip
            return (
                <Cell {...props}>
                    <span>{rowData.fullName}</span>
                </Cell>
            );
        }

        return (
            <Cell {...props}>
                <EmployeeDataviewTooltip
                    employeeName={row.employeeName}
                    dataviewAttributes={row.dataviewAttributes}
                />
            </Cell>
        );
    };

    /**
     * @param {string} newSortColumn
     * @param {CommonClient2.SortType} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        setSortType(newSortType);
    };

    /**
     * @param {string[]} selectedColumns
     */
    const handleDataviewFilterChange2 = (selectedColumns) => {
        const newColumnSelection = [...managerAppData.dataviewFields].map((x) => ({ ...x, checked: selectedColumns.includes(x.value) }));

        const filteredDataviewData = managerAppData.dataviewEmployeeData.map((e) => {
            const selectedDataview = e.dataviewAttributes.filter((x) => selectedColumns.includes(x.key));
            return ({
                ...e,
                dataviewAttributes: selectedDataview,
            });
        });

        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: { dataviewFields: newColumnSelection, filteredDataviewEmployeeData: filteredDataviewData },
        });
    };

    /**
     * @param {Date[]} updatedValue
     * @param {any} event
     */
    const handleApplyDateRangePicker = (updatedValue, event) => {
        const [selectedStartDate, selectedEndDate] = updatedValue;
        const startDate = selectedStartDate;
        const endDate = selectedEndDate;
        const selectedPeriod = {
            startDate,
            endDate,
        };

        removeValidationError(Fields.PeriodSelected, selectedPeriod);
        if (isDraft) {
            dispatch(selectPeriod(
                isDraft,
                bidProfileDraft.draftId,
                bidProfileDraft.employees.hyperfindId,
                selectedPeriod,
                bidProfileDraft.originalBidProfileInformation.bidSetupId,
                { managerBidSetups },
            ));
        } else {
            dispatch(selectPeriod(
                isDraft,
                bidProfile.profileId,
                bidProfile.employees.hyperfindId,
                selectedPeriod,
                bidProfile.originalBidProfileInformation.bidSetupId,
                { managerBidSetups },
            ));
        }
    };

    const handleSearchValueChange = (e) => {
        setPageIndex(1);
        setSearchValue(e.target.value);
    };
    /**
     * @param {number} size 
     */
    const handleChangePageSize = (size) => {
        setPageIndex(1);
        setPageSize(size);
    };

    /**
     * @param {number} pageNumber 
     */
    const handleChangePageIndex = (pageNumber) => {
        setPageIndex(pageNumber);
    };

    return (
        <Accordion
            expanded={employees.isExpanded}
            onChange={(event, expanded) => toggleAccordion(expanded)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Avatar className="bde-avatar">{3}</Avatar>
                <Typography variant="h6">{t('form.employeeSelectionHeader')}</Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails className="employee-accordion-details">
                <div className="select-employees-content">
                    <Grid
                        container
                        spacing={2}
                    >
                        {isEmployeeSelectionDateRangeRequired && (
                            <Grid
                                item
                                xs={6}
                            >
                                <ObsoleteRangePicker
                                    name="employeeSelectionPeriod"
                                    value={[
                                        employees.periodSelected.startDate,
                                        employees.periodSelected.endDate,
                                    ]}
                                    ranges={allowedSymbolicPeriods}
                                    onChange={handleApplyDateRangePicker}
                                    disabled={isInputDisabled}
                                    minDate={currentDate}
                                    hasError={validationErrors.periodSelected}
                                    applyButtonClasses="date-range-picker-apply-button"
                                    locale={{
                                        applyLabel: t('form.button.dateRangePickerApplyLabel'),
                                        cancelLabel: t('form.button.dateRangePickerCancelLabel'),
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid
                            item
                            xs={isEmployeeSelectionDateRangeRequired ? 6 : 12}
                        >
                            <AutoComplete
                                id="hyperfind-select"
                                value={
                                    managerAppData.hyperfinds.find((x) => x.value === employees.hyperfindId)
                                    || { label: '', value: null }
                                }
                                options={managerAppData.hyperfinds}
                                onChange={(event, optionSelected) => handleHyperfindSelectionChange(Fields.HyperfindId, optionSelected)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label={t('form.hyperfind')}
                                    />
                                )}
                                getOptionLabel={(option) => option.label}
                                includeInputInList
                                disabled={isInputDisabled || isRebid}
                                disableClearable
                                className={validationErrors.hyperfindId ? 'form-input-error' : ''}
                            />
                            {
                                validationErrors.hyperfindId ? <span style={{ color: 'red' }}>{t('form.error.hyperfindId')}</span> : null
                            }
                        </Grid>
                        <Grid
                            item
                            xs={12}
                        >
                            <span className="employees-included-header">{t('form.employeesIncludedInBid')}</span>
                            <br />
                            <div className={!checkedEmployees.length ? 'error-text' : ''}>
                                <span className="employees-included-number">{checkedEmployees.length}</span>
                                <span>
                                    {checkedEmployees.length === 1 ? ` ${t('form.employee')}` : ` ${t('form.employees')}`}
                                    {' '}
                                </span>
                            </div>
                        </Grid>
                        <Grid
                            item
                            xs={4}
                        >
                            <TextField
                                id="search-bar-input"
                                label={t('form.searchEmployee')}
                                variant="outlined"
                                value={searchValue}
                                onChange={handleSearchValueChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search className="blue-icon" />
                                        </InputAdornment>),
                                }}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            className="dataview-filters-container"
                        >
                            <ColumnFilter
                                options={managerAppData.dataviewFields}
                                onOptionSelected={handleDataviewFilterChange2}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                        >
                            <Table
                                id="table"
                                data={paginatedData}
                                sortType={sortType}
                                sortColumn={sortColumn}
                                onSortColumn={handleSortColumn}
                                height={420}
                            >
                                <Column
                                    width={50}
                                    align="center"
                                    resizable
                                >
                                    <HeaderCell>
                                        <div style={{ lineHeight: '40px' }}>
                                            <RSCheckbox
                                                inline
                                                checked={isSelectAllChecked}
                                                indeterminate={isSelectAllIndeterminate}
                                                onChange={handleCheckAll}
                                                disabled={isInputDisabled}
                                            />
                                        </div>
                                    </HeaderCell>
                                    <Cell>
                                        {/** @param {ManagerAppData.IEmployee} rowData */
                                            (rowData) => (
                                                <div style={{ lineHeight: '46px' }}>
                                                    <RSCheckbox
                                                        value={rowData.employeeId}
                                                        inline
                                                        onChange={handleCheck}
                                                        checked={checkedEmployees.some((item) => item === rowData.employeeId)}
                                                        disabled={isInputDisabled}
                                                    />
                                                </div>
                                            )
                                        }
                                    </Cell>
                                </Column>
                                <Column
                                    width={160}
                                    resizable
                                    sortable
                                >
                                    <HeaderCell>{t('form.employeeName')}</HeaderCell>
                                    {
                                        // @ts-ignore
                                        <NameCell dataKey={employeeNameDataKey} />
                                    }

                                </Column>
                                {
                                    managerAppData.dataviewFields
                                        .filter((x) => x.checked)
                                        .map((element) => (
                                            <Column
                                                key={element.key}
                                                width={160}
                                                resizable
                                                sortable
                                            >
                                                <HeaderCell>{element.label}</HeaderCell>
                                                <Cell dataKey={element.key}>
                                                    {
                                                        /** @param {ManagerAppData.IEmployee} rowData */
                                                        (rowData) => rowData.dataviewAttributes.find((x) => x.key === element.key)?.value
                                                    }
                                                </Cell>
                                            </Column>
                                        ))
                                }
                            </Table>
                            <div className="pagination-wrapper">
                                <Pagination
                                    prev
                                    next
                                    first
                                    last
                                    ellipsis
                                    boundaryLinks
                                    maxButtons={5}
                                    size="xs"
                                    layout={['total', '-', 'pager', 'skip', '|', 'limit']}
                                    total={sortedData.length}
                                    limitOptions={pageSizeOptions}
                                    limit={pageSize}
                                    onChangeLimit={handleChangePageSize}
                                    activePage={pageIndex}
                                    onChangePage={handleChangePageIndex}
                                />
                            </div>
                            {
                                validationErrors.employeesIds ? <span style={{ color: 'red' }}>{t('form.error.employeesIds')}</span> : null
                            }
                            {
                                validationErrors.bidSetupId ? <span style={{ color: 'red' }}>{t('form.error.bidSetup')}</span> : null
                            }
                        </Grid>
                    </Grid>

                </div>
            </AccordionDetails>
            {!hasOnlyViewAccess && (
                <>
                    <Divider />
                    <AccordionActions className="employee-selection-accordion-actions">
                        {isShowEmployeeSelectionWarning(profile) && (
                            <ManagerWarning
                                warnings={managerWarnings}
                                t={t}
                            />
                        )}
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={handleDiscard}
                            disabled={isInputDisabled}
                        >
                            {t('globalForm.discard')}
                        </Button>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleConfirm}
                            disabled={isInputDisabled || !canEditEmployees}
                            className="save-continue--button"
                        >
                            <FontAwesomeIcon icon={faCheckCircle} />
                            {t('globalForm.applyButton')}
                        </Button>
                    </AccordionActions>
                </>
            )}
        </Accordion>
    );
}
export default EmployeeSelection;
